import {
  EDocumentData,
  EFirebaseContext,
  ERef,
  ESnapshotExists,
  Event
} from '../types';
import {
  DocumentEventsSearchRequest,
  isOrderSyncTriggerEventType,
  OrderEvent
} from '../types/events';
import { getOrThrow } from '../utils/refs';
import { CollectionName } from '../constants';
import { ResponseOrError, wrapError, wrapSuccess } from '../types/responses';
import { safeAsync } from '../safeWrappers';
import { removeUndefinedFields } from '../helpers';
import { ColumnService } from './directory';
import { getErrorReporter } from '../utils/errors';

export const eventRefDocumentTypes = {
  advertiserOrganization: 'Advertiser Organization',
  invite: 'Invite',
  invoice: 'Invoice',
  newspaper: 'Newspaper',
  notice: 'Notice',
  order: 'Order',
  publicationIssue: 'Publication Issue',
  user: 'User'
};

export type EventRefDocumentType =
  (typeof eventRefDocumentTypes)[keyof typeof eventRefDocumentTypes];

export const documentTypeToCollection = (
  documentType: EventRefDocumentType
): CollectionName => {
  switch (documentType) {
    case 'invoice':
      return 'invoices';
    case 'notice':
      return 'usernotices';
    case 'newspaper':
      return 'organizations';
    case 'user':
      return 'users';
    case 'advertiserOrganization':
      return 'organizations';
    case 'publicationIssue':
      return 'publicationIssues';
    case 'order':
      return 'orders';
    case 'invite':
      return 'invites';
    default:
      throw new Error(`Unknown document type: ${documentType}`);
  }
};

const documentTypeToQueryProp = (
  documentType: EventRefDocumentType
): string => {
  switch (documentType) {
    case 'invoice':
      return 'data.invoice';
    case 'notice':
      return 'notice';
    case 'newspaper':
      return 'newspaper';
    case 'user':
      return 'user';
    case 'advertiserOrganization':
      return 'organization';
    case 'order':
      return 'order';
    case 'publicationIssue':
      return 'publicationIssue';
    case 'invite':
      return 'invite';
    default:
      throw new Error(`Unknown document type: ${documentType}`);
  }
};

export class EventService {
  constructor(private context: EFirebaseContext) {}

  public async getEventsForDocument(
    query: DocumentEventsSearchRequest
  ): Promise<ESnapshotExists<Event>[]> {
    const { documentId, documentType } = query;
    const queryProp = documentTypeToQueryProp(documentType);
    const collectionName = documentTypeToCollection(documentType);
    const ref = this.context.doc(`${collectionName}/${documentId}`);
    await getOrThrow(ref);
    const eventQuery = this.context.eventsRef().where(queryProp, '==', ref);
    const queryResults = await eventQuery.get();
    return queryResults.docs;
  }

  public async createOrderEvent<T extends OrderEvent>(
    documentData: Omit<T, 'createdAt'>
  ): Promise<ResponseOrError<ERef<T>>> {
    const [eventError, event] = await safeAsync(async () =>
      this.context.eventsRef<T>().add({
        ...documentData,
        data: removeUndefinedFields(documentData.data),
        createdAt: this.context.fieldValue().serverTimestamp()
      } as EDocumentData<T>)
    )();
    if (eventError) {
      if (isOrderSyncTriggerEventType(documentData.type)) {
        getErrorReporter().logAndCaptureCriticalError(
          ColumnService.ORDER_MANAGEMENT,
          eventError,
          `Failed to create order event of type that may impact syncing`,
          {
            orderId: documentData.order.id,
            eventType: documentData.type
          }
        );
      } else {
        getErrorReporter().logAndCaptureError(
          ColumnService.ORDER_MANAGEMENT,
          eventError,
          `Failed to create order event`,
          {
            orderId: documentData.order.id,
            eventType: documentData.type
          }
        );
      }
      return wrapError(eventError);
    }
    return wrapSuccess(event);
  }
}
